import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Stack,
  Typography,
  Box,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Menu,
  Modal,
  Skeleton,
} from "@mui/material";

import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import { ReactComponent as LoadingIcon } from "../../../../../vendor/_new/icon/loading.svg";

import { GreenSelectForm, GreenSwitch } from "../../../../common/Greens";
import PermissionGate from "../../../../guards/PermissionGate";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { tableHeadSx, tableBodySx } from "../../detection/WAFEvent";
import { getExpressionKeyTitle } from "../../../../../utils/constants";
import { FirewallAction } from "../../../../../utils/constants";
import { formatDate } from "../../../../../utils/format";
import { generateMenuSx } from "../../../../../layouts/_new/Normal";
import { CancelButton, GhostButton, PrimaryButton, SaveButton } from "../../../../common/Button";
import FirewallRuleModal from "./FirewallRuleModal";
import { useNotification } from "../../../../../contexts/NotificationContext";
import StickySaveBar from "../../../../common/StickySaveBar";
import { cloneItems, collectRuleEnabledDiff } from "../../../../common/stickySaveBarUtils";

import useAuth from "../../../../../hooks/useAuth";
import useFirewall from "../../../../../hooks/user/useFirewall";
import useSite from "../../../../../hooks/user/useSite";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import usePermissions from "../../../../../hooks/usePermissions";

const getAction = (code) => {
  switch (code) {
    case FirewallAction.LOG:
      return "LOG";
    // case FirewallAction.BYPASS:
    //   return "BYPASS";
    case FirewallAction.ALLOW:
      return "ALLOW";
    case FirewallAction.CHALLENGE:
      return "CHALLENGE";
    case FirewallAction.BLOCK:
      return "BLOCK";
    case FirewallAction.DROP:
      return "DROP";
    default:
      break;
  }
};

const getOriginLabel = (isAutomatic) => {
  if (isAutomatic === true) return "Automatic";
  if (isAutomatic === false) return "Manual";
  return "—";
};

const toolbarGhostButtonSx = {
  height: 40,
  minHeight: 40,
  minWidth: 112,
  px: 2,
  fontSize: 14,
  lineHeight: "21px",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": { mr: 1 },
};

const toolbarPrimaryButtonSx = {
  height: 40,
  minHeight: 40,
  minWidth: 180,
  px: 2,
  py: 0,
  fontSize: 14,
  lineHeight: "21px",
  fontWeight: 400,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": { mr: 1 },
};

const NewServicesFirewallRules = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, getPermissionsForSite, PERMISSIONS } = usePermissions();
  const { getFirewalls, updateFirewall, firewalls, deleteFirewall, errMsg, setErr } = useFirewall();
  const { siteList, getSitesForItems } = useSite();
  const { showSuccess, showError } = useNotification();

  const { effectiveSiteId, effectiveSiteUid } = useGlobalSite();
  const siteID = effectiveSiteId ?? "";
  const siteUid = effectiveSiteUid;
  const [savedFirewalls, setSavedFirewalls] = useState(null);
  const [workingFirewalls, setWorkingFirewalls] = useState(null);

  const selectedSite = useMemo(() => siteList?.find((s) => s.site_id === siteID) ?? null, [siteList, siteID]);

  const { canReadFirewallForSite, canWriteFirewallForSite, canDeleteFirewallForSite } = useMemo(() => {
    if (!selectedSite) {
      return { canReadFirewallForSite: false, canWriteFirewallForSite: false, canDeleteFirewallForSite: false };
    }
    const sp = getPermissionsForSite(selectedSite);
    return {
      canReadFirewallForSite: sp.hasPermission(PERMISSIONS.FIREWALL_READ),
      canWriteFirewallForSite: sp.hasPermission(PERMISSIONS.FIREWALL_WRITE),
      canDeleteFirewallForSite: sp.hasPermission(PERMISSIONS.FIREWALL_DELETE),
    };
  }, [selectedSite, getPermissionsForSite, PERMISSIONS]);

  useEffect(() => {
    if (!canReadFirewallForSite) return;
    if (firewalls) {
      setSavedFirewalls(firewalls);
      setWorkingFirewalls(firewalls);
    }
  }, [firewalls, canReadFirewallForSite]);

  const [isInitialized, setIsInitialized] = useState(true);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getSitesForItems().then(() => setIsInitialized(false));
    }
    return () => setErr(null);
  }, [isAuthenticated, getSitesForItems, setErr]);

  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSavingToggles, setIsSavingToggles] = useState(false);

  useEffect(() => {
    if (!siteUid) return;
    if (!canReadFirewallForSite) {
      setWorkingFirewalls([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setWorkingFirewalls(null);
    getFirewalls(siteUid, true).then(() => setLoading(false));
  }, [siteUid, getFirewalls, canReadFirewallForSite]);

  useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  const [firewallRuleToEdit, setFirewallRuleToEdit] = useState(null);
  const [prefilledRule, setPrefilledRule] = useState(null);
  const [openFirewallRule, setOpenFirewallRule] = useState(false);
  const handleFirewallRuleOpen = () => setOpenFirewallRule(true);
  const handleFirewallRuleClose = () => {
    setOpenFirewallRule(false);
    setFirewallRuleToEdit(null);
    setPrefilledRule(null);
  };

  useEffect(() => {
    const state = location.state;
    if (!state?.openCreate || !state?.prefilled) return;
    setFirewallRuleToEdit(null);
    setPrefilledRule(state.prefilled);
    setOpenFirewallRule(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const [actionMenu, setActionMenu] = useState({
    anchorEl: null, // the DOM element the menu anchors to
    firewallRuleId: null, // index of subrow (or null if main row)
  });
  // open handler (call from the ActionIcon onClick)
  const handleActionOpen = (event, firewallRuleId = null) => {
    event.stopPropagation(); // prevent row expand/click side effects
    setActionMenu({ anchorEl: event.currentTarget, firewallRuleId });
  };

  // close handler
  const handleActionClose = () => {
    setActionMenu({ anchorEl: null, firewallRuleId: null });
  };

  const isActionMenuOpen = Boolean(actionMenu.anchorEl);

  const [deleteFirewallId, setDeleteFirewallId] = useState();
  const [openFirewallRuleToDelete, setOpenFirewallRuleToDelete] = useState(false);
  const handleFirewallRuleToDeleteOpen = () => setOpenFirewallRuleToDelete(true);
  const handleFirewallRuleToDeleteClose = () => setOpenFirewallRuleToDelete(false);

  const handleDelete = async () => {
    if (!canDeleteFirewallForSite) return;
    setLoading(true);
    try {
      await deleteFirewall(siteUid, { fw_rule_id: deleteFirewallId });
    } catch (err) {
      console.error(err);
    }
    handleFirewallRuleToDeleteClose();
    setLoading(false);
  };

  const stickyModifiedFields = useMemo(
    () => collectRuleEnabledDiff(savedFirewalls, workingFirewalls),
    [savedFirewalls, workingFirewalls],
  );
  const stickyModifiedCount = stickyModifiedFields.length;

  const changeFirewallEnable = (event, id) => {
    if (!canWriteFirewallForSite || isSavingToggles) return;
    const enabled = event.target.checked;
    setWorkingFirewalls((prev) =>
      (prev || []).map((item) => (item?.id === id ? { ...item, enabled } : item)),
    );
  };

  const discardToggleChanges = useCallback(() => {
    setWorkingFirewalls(cloneItems(savedFirewalls));
  }, [savedFirewalls]);

  const applyToggleChanges = useCallback(async () => {
    if (!canWriteFirewallForSite || !siteUid || stickyModifiedCount === 0) return;
    const savedById = Object.fromEntries((savedFirewalls || []).map((r) => [r.id, !!r.enabled]));
    const changed = (workingFirewalls || []).filter((r) => savedById[r.id] !== !!r.enabled);
    if (changed.length === 0) return;

    setIsSavingToggles(true);
    try {
      await Promise.all(
        changed.map((r) => updateFirewall(siteUid, r.id, { enabled: !!r.enabled })),
      );
      setSavedFirewalls(cloneItems(workingFirewalls));
      showSuccess(`Saved ${changed.length} firewall rule${changed.length === 1 ? "" : "s"}.`);
    } catch (err) {
      showError(err?.message || "Failed to update firewall rules");
    } finally {
      setIsSavingToggles(false);
    }
  }, [
    canWriteFirewallForSite,
    siteUid,
    stickyModifiedCount,
    savedFirewalls,
    workingFirewalls,
    updateFirewall,
    showError,
    showSuccess,
  ]);

  const refresh = async () => {
    if (siteUid) {
      setIsRefreshing(true);
      setLoading(true);
      await getFirewalls(siteUid, true);
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  return (
    <PermissionGate permission={PERMISSIONS.FIREWALL_READ} resourceLabel="firewall rules">
    <>
      <Helmet title="Firewall Rules" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="Firewall Rules"
          helpTooltip="Create IP, country, or expression-based firewall rules evaluated before WAF. Allow, block, challenge, or log matching traffic at the edge."
          sx={{ pb: 0, alignItems: "center" }}
          actions={
            siteID && canReadFirewallForSite ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <GhostButton
                  disabled={isRefreshing || loading}
                  startIcon={
                    isRefreshing ? (
                      <LoadingIcon style={{ color: colors.neutral[600], width: 16, height: 16 }} />
                    ) : (
                      <LayerdIcon icon={UI_ICONS.refresh} size="sm" color="neutral" />
                    )
                  }
                  onClick={refresh}
                  sx={toolbarGhostButtonSx}
                >
                  Refresh
                </GhostButton>
                {canWriteFirewallForSite && (
                  <PrimaryButton
                    startIcon={<LayerdIcon icon={UI_ICONS.plus} size="sm" color="inherit" />}
                    onClick={() => {
                      setFirewallRuleToEdit(null);
                      handleFirewallRuleOpen();
                    }}
                    sx={toolbarPrimaryButtonSx}
                  >
                    Add firewall rule
                  </PrimaryButton>
                )}
              </Stack>
            ) : null
          }
        />
        <SiteScopeGate mode="single">
          {() => (
            <>
        {!canReadFirewallForSite && selectedSite && (
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[600] }}>
            You do not have permission to view firewall rules for this site in your project role.
          </Typography>
        )}
        {canReadFirewallForSite && (
          <Box sx={{ width: "100%", borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}`, p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 2, px: 3 },
                "& .MuiTableCell-body": { py: 1.5, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "8%" }}>
                    <Typography sx={tableHeadSx}>No</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "32%" }}>
                    <Typography sx={tableHeadSx}>Name</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "10%" }}>
                    <Typography sx={tableHeadSx}>Action</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "12%" }}>
                    <Typography sx={tableHeadSx}>Origin</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "14%" }}>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "14%" }}>
                    <Typography sx={tableHeadSx}>Status</Typography>
                  </TableCell>
                  <TableCell sx={{ width: "10%" }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workingFirewalls === null &&
                  [1, 2, 3, 4, 5]?.map((index) => (
                    <TableRow key={`ske_${index}`}>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                      <TableCell colSpan={2}>
                        <Skeleton height="22px" width="100%" py="5px" variant="rectangular" sx={{ borderRadius: "11px" }} />
                      </TableCell>
                    </TableRow>
                  ))}
                {workingFirewalls?.map((row, index) => (
                  <TableRow key={`firewall_rule_${index}`}>
                    <TableCell>
                      <Typography sx={tableBodySx}>{index + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{row?.name ? row?.name : row?.id}</Typography>
                      <Typography sx={{ ...tableBodySx, color: colors.neutral[600] }}>
                        {row?.fields && row?.fields.map((value) => getExpressionKeyTitle(value)).join(", ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{getAction(row?.action)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{getOriginLabel(row?.is_automatic)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{formatDate(row?.created_at) || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <GreenSwitch
                        checked={row?.enabled}
                        disabled={!canWriteFirewallForSite || isSavingToggles}
                        onChange={(e) => changeFirewallEnable(e, row?.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                        <Box
                          onClick={(e) => (canWriteFirewallForSite || canDeleteFirewallForSite) && handleActionOpen(e, row?.id)}
                          sx={{
                            cursor: canWriteFirewallForSite || canDeleteFirewallForSite ? "pointer" : "not-allowed",
                            width: 24,
                            height: 24,
                            opacity: canWriteFirewallForSite || canDeleteFirewallForSite ? 1 : 0.4,
                          }}
                        >
                          <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {workingFirewalls !== null && workingFirewalls?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 3, borderBottom: "none" }}>
                      <Typography sx={{ ...tableBodySx, textAlign: "center" }}>
                        There are no firewall rules
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
            </>
          )}
        </SiteScopeGate>
      </Stack>
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={isActionMenuOpen}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 200,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              if (!canWriteFirewallForSite) return;
              const { firewallRuleId } = actionMenu;
              setFirewallRuleToEdit(workingFirewalls?.find((item) => item?.id === firewallRuleId));
              handleFirewallRuleOpen();
              handleActionClose();
            }}
            sx={{
              ...generateMenuSx(176, 37),
              opacity: canWriteFirewallForSite ? 1 : 0.4,
              cursor: canWriteFirewallForSite ? "pointer" : "not-allowed",
              pointerEvents: canWriteFirewallForSite ? "auto" : "none",
            }}
          >
            <LayerdIcon icon={UI_ICONS.edit} size={18} color="neutral" />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Edit</Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              if (!canDeleteFirewallForSite) return;
              const { firewallRuleId } = actionMenu;
              setDeleteFirewallId(firewallRuleId);
              handleFirewallRuleToDeleteOpen();
              handleActionClose();
            }}
            sx={{
              ...generateMenuSx(176, 37),
              opacity: canDeleteFirewallForSite ? 1 : 0.4,
              cursor: canDeleteFirewallForSite ? "pointer" : "not-allowed",
              pointerEvents: canDeleteFirewallForSite ? "auto" : "none",
            }}
          >
            <LayerdIcon icon={UI_ICONS.delete} size={18} color="neutral" />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Delete
            </Typography>
          </Stack>
        </Box>
      </Menu>
      <Modal open={openFirewallRuleToDelete} onClose={handleFirewallRuleToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Firewall Rule?
            </Typography>
            <Box onClick={handleFirewallRuleToDeleteClose} sx={{ cursor: "pointer" }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Are you sure you want to permanently delete this firewall rule?
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleFirewallRuleToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDelete} disabled={loading || !canDeleteFirewallForSite} label="Delete" />
          </Stack>
        </Stack>
      </Modal>
      <FirewallRuleModal
        siteUid={siteUid}
        open={openFirewallRule}
        handleClose={handleFirewallRuleClose}
        firewallToEdit={firewallRuleToEdit}
        prefilled={prefilledRule}
        canWriteFirewallForSite={canWriteFirewallForSite}
      />
      <StickySaveBar
        open={canWriteFirewallForSite && stickyModifiedCount > 0}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={discardToggleChanges}
        onApply={applyToggleChanges}
        isApplying={isSavingToggles}
      />
    </>
    </PermissionGate>
  );
};
export default NewServicesFirewallRules;
